<template>
    <div>
        <h2 class="title is-3">
            <b-icon
                icon="table-arrow-right" />
            Export Code Tables
        </h2>
        <spinner :loading="loading">
            <b-field grouped>
                <b-field label="Tag/Group:">
                    <b-select
                        id="group"
                        v-model="groupFilter"
                        :placeholder="groups.length + ' groups'"
                        @update:model-value="didChangeGroup($event)">
                        <option
                            v-for="s in groups"
                            :key="s"
                            :value="s">
                            {{ s }}
                        </option>
                    </b-select>
                </b-field>
                <b-field label="Scope type:">
                    <b-select
                        id="scopeType"
                        v-model="selectedScopeType"
                        :placeholder="scopeTypes.length + ' scope types'"
                        name="Scope"
                        @update:model-value="loadScopes($event)">
                        <option
                            v-for="st in scopeTypes"
                            :key="st"
                            :value="st">
                            {{ st }}
                        </option>
                    </b-select>
                </b-field>
                <b-field :label="selectedScopeType ? selectedScopeType + ':' : 'Scopes:'">
                    <b-select
                        id="scope"
                        v-model="selectedScope"
                        :placeholder="scopes.length + ' ' + (selectedScopeType ? selectedScopeType + 's' : 'scopes')"
                        name="Scope"
                        @update:model-value="didChangeScope($event)">
                        <option
                            v-for="s in scopes"
                            :key="s"
                            :value="s">
                            {{ s }}
                        </option>
                    </b-select>
                </b-field>
                <b-field label="Library name filter:">
                    <b-input
                        v-model="libraryFilter"
                        placeholder="filter text" />
                </b-field>
                <b-field label="Filter valid codes:">
                    <b-switch
                        v-model="getValidOnly"
                        style="top: 10%">
                        {{ getValidOnly ? 'Only valid codes will be exported' : 'All codes will be exported' }}
                    </b-switch>
                </b-field>
            </b-field>
            <b-field grouped>
                <b-field>
                    <b-button
                        @click="selectAllLibraries()">
                        <i
                            class="mdi mdi-select-all"
                            aria-hidden="true" />
                        Select all
                    </b-button>
                </b-field>
                <b-field>
                    <b-button
                        @click="deselectAllLibraries()">
                        <i
                            class="mdi mdi-select-off"
                            aria-hidden="true" />
                        Deselect all
                    </b-button>
                </b-field>
                <b-field>
                    <b-button
                        @click="inverseSelectedLibraries()">
                        <i
                            class="mdi mdi-select-inverse"
                            aria-hidden="true" />
                        Inverse selected
                    </b-button>
                </b-field>
                <b-field>
                    <b-button
                        type="is-primary"
                        icon-left="download"
                        @click="downloadReport">
                        Export selected tables
                    </b-button>
                </b-field>
            </b-field>
            <hr>
            <div class="table-container">
                <b-field>
                    <b-input
                        v-model="searchFilter"
                        placeholder="Type to filter..."
                        icon-right="close-circle"
                        icon-right-clickable
                        @icon-right-click="searchFilter=''" />
                </b-field>
                <b-table
                    sticky-header
                    height="max-content"
                    :data="librariesSearched">
                    <b-table-column
                        v-slot="slotProps"
                        field="selection"
                        width="0">
                        <b-checkbox
                            v-model="selectedLibraries"
                            :native-value="slotProps.row.name" />
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        field="name"
                        label="Library">
                        {{ slotProps.row.name }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        field="alias"
                        label="Alias">
                        {{ slotProps.row.alias }}
                    </b-table-column>
                </b-table>
            </div>
            <b-loading
                v-model="generating"
                :is-full-page="false"
                :can-cancel="false" />
        </spinner>
    </div>
</template>
<script>
    import Spinner from '@/shared/components/Spinner.vue';
    import { getAllTagsAsEntities, getCodeTables, getLibraries, getScopes } from '@/shared/helpers/api';
    import { showMixin } from '@/shared/mixins/showMixin';
    import saveAs from 'file-saver';
    import _ from 'lodash';

    export default {
        components: {
            Spinner
        },
        mixins: [showMixin],
        url: {
            selectedScopeType: {
                param: 'scopeType',
                noHistory: true
            },
            selectedScope: {
                param: 'scope',
                noHistory: true
            },
            groupFilter: {
                param: 'group',
                noHistory: true
            },
            groupFilterAlias: {
                param: 'tag',
                noHistory: true
            },
            selectedLibrariesSerialized: {
                param: 'library',
                noHistory: true
            }
        },
        data() {
            return {
                loading: true,
                loadedFromStorage: false,
                generating: false,
                scopes: [],
                libraries: [],
                groups: [],
                selectedLibrariesSerialized: null,
                selectedScope: null,
                selectedScopeType: null,
                libraryFilter: '',
                groupFilter: null,
                groupFilterAlias: null,
                scopeTypes: [],
                getValidOnly: true,
                searchFilter: ''
            };
        },
        computed: {
            librariesFiltered() {
                const filterText = this.libraryFilter?.trim()?.toLowerCase();
                return filterText
                    ? this.libraries.filter(lib => lib.filterData.includes(filterText))
                    : this.libraries;
            },
            librariesSearched() {
                return this.librariesFiltered.filter(lib =>
                    lib.name
                        .concat('|')
                        .concat(lib.alias)
                        .toLowerCase()
                        .includes(this.searchFilter.toLowerCase()));
            },
            selectedLibraries: {
                get() {
                    if (this.selectedLibrariesSerialized && this.selectedLibrariesSerialized.length > 0) {
                        return this.selectedLibrariesSerialized.split(',');
                    }
                    return [];
                },
                set(val) {
                    this.selectedLibrariesSerialized = _.intersection(val, this.librariesFiltered.map(lib => lib.name))
                        .join(',');
                }
            },
            dataToPersist() {
                return {
                    group: this.groupFilter,
                    scopeType: this.scopeType,
                    scope: this.selectedScope,
                    selectedLibraries: this.selectedLibraries
                };
            }
        },
        watch: {
            dataToPersist() {
                if (this.loadedFromStorage) { // Avoid writing while loading from storage
                    localStorage.setItem('codeTables.exportOptions', JSON.stringify(this.dataToPersist));
                }
            }
        },
        async mounted() {
            if (this.groupFilterAlias && !this.groupFilter) {
                this.groupFilter = this.groupFilterAlias;
                this.groupFilterAlias = null;
            } else if (this.groupFilter) {
                this.groupFilterAlias = null;
            }

            const options = JSON.parse(localStorage.getItem('codeTables.exportOptions'));

            if (!this.groupFilter && options?.group) {
                this.groupFilter = options.group;
            }
            if (!this.selectedScopeType && options?.scopeType) {
                this.selectedScopeType = options.scopeType;
            }
            if (!this.selectedScope && options?.scope) {
                this.selectedScope = options.scope;
            }

            await this.loadGroups();
            await this.loadLibraries();

            // this must be set after libraries are loaded, as the custom setter disallows selecting non-loaded libraries
            if (this.selectedLibraries.length === 0 && options?.selectedLibraries?.length > 0) {
                this.selectedLibraries = options.selectedLibraries;
            }

            await this.loadScopeTypes();

            this.loadedFromStorage = true;
            this.loading = false;
        },
        methods: {
            async loadScopes(scopeType) {
                if (!scopeType) {
                    this.scopes = [];
                    this.selectedScope = null;
                    return;
                }
                this.scopes = (await getScopes(this, scopeType)).map(s => s.identity).sort();
                if (!this.selectedScope) {
                    this.selectedScope = this.scopes.length > 0 ? this.scopes[0] : null;
                }
            },
            async didChangeGroup() {
                this.groupFilterAlias = null;
                this.loading = true;
                this.scopes = [];
                this.scopeTypes = [];
                const tempScope = this.selectedScope;
                this.selectedScope = null;
                this.selectedScopeType = null;
                await this.loadLibraries();
                await this.loadScopeTypes();

                if (this.scopes.includes(tempScope)) {
                    this.selectedScope = tempScope;
                }

                this.loading = false;
            },
            async didChangeScope(scope) {
                this.loading = true;

                await this.loadLibraries(scope);

                this.loading = false;
            },
            async loadGroups() {
                this.groups = (await getAllTagsAsEntities(this)).map(g => g.name).sort();
            },
            async loadLibraries(scope = '') {
                const temp = _.sortBy(await getLibraries(this, scope, this.groupFilter), 'name');
                temp.forEach(this.setFilterData);
                this.libraries = temp;
            },
            async loadScopeTypes() {
                this.scopeTypes = _.chain(this.libraries)
                    .map(lib => lib.scopeType)
                    .filter(lib => lib)
                    .uniq()
                    .value();
                if (!this.selectedScopeType) {
                    this.selectedScopeType = this.scopeTypes.length > 0 ? this.scopeTypes[0] : null;
                }
                await this.loadScopes(this.selectedScopeType);
            },
            async downloadReport() {
                this.generating = true;

                // Removes libs that are not available for this scope or group
                // This looks strange, but was probably done intentionally. See computed implementation..
                // Should be refactored for clarity.
                // eslint-disable-next-line no-self-assign
                this.selectedLibraries = this.selectedLibraries;

                try {
                    const blob = await getCodeTables(this, this.selectedScope, this.selectedLibraries, this.getValidOnly);

                    if (blob) {
                        saveAs(blob, 'code_tables.xlsx');
                        return true;
                    }
                } catch (err) {
                    this.showError(err);
                // handled in transformResponse :<
                } finally {
                    this.generating = false;
                }
            },
            selectAllLibraries() {
                this.selectedLibraries = this.libraries.map(l => l.name);
            },
            deselectAllLibraries() {
                this.selectedLibraries = [];
            },
            inverseSelectedLibraries() {
                this.selectedLibraries = this.libraries.map(l => l.name).filter(e => !this.selectedLibraries.includes(e));
            },
            setFilterData(library) {
                if (library.alias) {
                    library.filterData = `${library.name} ${library.alias}`.toLowerCase();
                } else {
                    library.filterData = library.name.toLowerCase();
                }
            }
        }
    };
</script>

<style scoped>
.table-container {
    width: 800px;
}
.table-container:deep(.table-wrapper) {
    max-height: 60vh;
}
</style>
